import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icon';

/**
 * One picker, for everything.
 *
 * There are 36 native `<select>` elements in this app and an `<option>` can hold text and nothing
 * else — which is why `'— '.repeat(depth)` is copy-pasted into six components to fake tree
 * indentation, why a goal picker cannot show the goal's colour, and why choosing a blocker gives
 * you no way to see it is already done.
 *
 * The honest cost of replacing them: a native select on a phone opens the operating system's own
 * picker, which is big, familiar, accessible and free. Owning that means owning all of it — the
 * ARIA 1.2 combobox pattern, arrow keys, Home/End, type-ahead, Escape, focus return, and a popup
 * that becomes a full-width sheet on a small screen rather than a 200-pixel dropdown. That is
 * exactly why this is *one* component: 36 bespoke comboboxes would be 36 chances to get it wrong.
 */

export interface Option<T = unknown> {
  /** Stable identity. `''` is reserved for the empty choice. */
  value: string;
  /** What search matches against, and the fallback rendering. */
  label: string;
  /** Tree depth, drawn as real indentation rather than em-dashes in a string. */
  depth?: number;
  disabled?: boolean;
  /** Anything the option renderer needs — the goal, the task, the template. */
  data?: T;
}

/**
 * How many options before a search box earns its place.
 *
 * By length, not by whether the list is "dynamic". A three-goal picker with a search box is
 * exactly the chrome P3 was spent deleting; meanwhile the *static* hour picker in Settings has
 * twenty-four options and wants one badly. Where the data came from is not the question.
 */
export const SEARCH_THRESHOLD = 8;

export function Select<T>({
  options,
  value,
  onChange,
  placeholder = 'None',
  renderOption,
  renderValue,
  ariaLabel,
  compact = false,
  id,
  allowEmpty = true,
  emptyLabel,
  disabled = false,
}: {
  options: Option<T>[];
  value: string | null;
  onChange: (next: string | null) => void;
  placeholder?: string;
  /** Rich rows — swatches, badges, counts. The reason this component exists. */
  renderOption?: (option: Option<T>) => ReactNode;
  /** How the chosen one is drawn in the closed control. Defaults to `renderOption`. */
  renderValue?: (option: Option<T>) => ReactNode;
  ariaLabel?: string;
  /**
   * A smaller trigger, sized to its content rather than filling its container.
   *
   * The *popup* stays full size deliberately — a compact list is simply a harder-to-read list, and
   * the trigger is the only part competing for room in a facet row. Still 32px tall, so still
   * clear of the 24×24 floor.
   */
  compact?: boolean;
  id?: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
  disabled?: boolean;
}) {
  const auto = useId();
  const base = id ?? auto;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const withSearch = options.length > SEARCH_THRESHOLD;

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const found = q === '' ? options : options.filter((o) => o.label.toLowerCase().includes(q));
    // The empty choice is offered only when nothing is being searched for — "None" matching the
    // query "no" would be a confusing first result.
    return allowEmpty && q === ''
      ? [{ value: '', label: emptyLabel ?? placeholder } as Option<T>, ...found]
      : found;
  }, [options, query, allowEmpty, emptyLabel, placeholder]);

  const selected = options.find((o) => o.value === value) ?? null;

  // Clamp rather than wrap, and never leave the marker past the end of a filtered list.
  useEffect(() => {
    setActive((a) => Math.min(Math.max(a, 0), Math.max(matches.length - 1, 0)));
  }, [matches.length]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Keep the marked row in view when the arrow keys run past the visible window.
  useEffect(() => {
    if (!open || !listRef.current) return;
    listRef.current.querySelector('.is-active')?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const openWith = (next: boolean) => {
    setOpen(next);
    if (next) {
      setQuery('');
      setActive(
        Math.max(
          matches.findIndex((o) => o.value === (value ?? '')),
          0,
        ),
      );
      // Focus moves to the search box when there is one; otherwise the button keeps it and drives
      // the list through `aria-activedescendant`.
      window.setTimeout(() => search.current?.focus(), 0);
    } else {
      // Focus must come back, or closing a picker drops the user at the top of the document.
      button.current?.focus();
    }
  };

  const choose = (option: Option<T>) => {
    if (option.disabled) return;
    onChange(option.value === '' ? null : option.value);
    setOpen(false);
    button.current?.focus();
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        openWith(true);
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      /*
        Handled here, so it stops here.

        A picker can sit inside a panel that also closes on Escape — the goal picker lives in the
        task filter panel — and without this one keystroke dismissed both, taking the panel away
        with the picker. `useDismiss` keeps a stack for surfaces that use it; this one says "that
        key was mine" for the surface that does not.
      */
      e.stopPropagation();
      return openWith(false);
    }
    if (e.key === 'Tab') return setOpen(false);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      return setActive((a) => Math.min(a + 1, matches.length - 1));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      return setActive((a) => Math.max(a - 1, 0));
    }
    if (e.key === 'Home') {
      e.preventDefault();
      return setActive(0);
    }
    if (e.key === 'End') {
      e.preventDefault();
      return setActive(matches.length - 1);
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const hit = matches[active];
      if (hit) choose(hit);
    }
  };

  const draw = renderValue ?? renderOption;

  return (
    <div className={`sel ${compact ? 'is-compact' : ''}`} ref={root}>
      <button
        ref={button}
        type="button"
        id={`${base}-btn`}
        className="sel-button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={`${base}-list`}
        {...(open && matches[active] ? { 'aria-activedescendant': `${base}-o${active}` } : {})}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        disabled={disabled}
        onClick={() => openWith(!open)}
        onKeyDown={onKey}
      >
        <span className={`sel-value ${selected ? '' : 'is-empty'}`}>
          {selected ? (
            draw ? (
              draw(selected)
            ) : (
              <span className="t">{selected.label}</span>
            )
          ) : (
            <span className="t">{placeholder}</span>
          )}
        </span>
        <span className="sel-caret">
          <Icon name="caret" size={14} />
        </span>
      </button>

      <div className="sel-pop" hidden={!open}>
        {withSearch && (
          <div className="sel-search">
            <input
              ref={search}
              type="search"
              value={query}
              placeholder="Search"
              aria-label="Search options"
              // The list is driven from here, so the same handler serves both focus positions.
              onKeyDown={onKey}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
            />
          </div>
        )}
        <ul className="sel-list" id={`${base}-list`} role="listbox" ref={listRef}>
          {matches.map((option, index) => (
            <li
              key={option.value}
              id={`${base}-o${index}`}
              role="option"
              aria-selected={option.value === (value ?? '')}
              aria-disabled={option.disabled || undefined}
              data-depth={option.depth ?? 0}
              className={`sel-opt ${index === active ? 'is-active' : ''}`}
              // `mousedown`, not `click`: the search box has focus, and a click would blur it
              // first and close the popup before the choice landed.
              onMouseDown={(e) => {
                e.preventDefault();
                choose(option);
              }}
              onMouseEnter={() => setActive(index)}
            >
              {option.value === '' || !renderOption ? (
                <span className="t">{option.label}</span>
              ) : (
                renderOption(option)
              )}
            </li>
          ))}
          {matches.length === 0 && <li className="sel-empty">Nothing matches “{query}”.</li>}
        </ul>
      </div>
    </div>
  );
}
